"use strict";

/**
 * This class acts as a thin wrapper over the database schema, so that the rest of the application can just
 * deal with business objects instead of database paths. Hence most of the functions in this class are of the form
 * "observeX", where X is a Business object. Internally we divert this call to the right path in firebase and start
 * the listeners.
 *
 * Another feature of this class is to manage listener lifetimes. In many cases it is hard to know the right set of
 * listeners which should be turned off when a particular UI stops showing. The way to deal with this is to give
 * each UI compoenent a new FirebaseDataSource, which maintains its own list of listners and they can be turned off
 * with a single call.
 *
 *
 * @param eventFirebase
 * @constructor
 */
var FirebaseDataSource = function (eventFirebase) {
    var This = this;
    This._eventFirebase = eventFirebase;
    This._listeners = [];
};

FirebaseDataSource.prototype.cloneSource = function () {
    var This = this;
    return new FirebaseDataSource(This._eventFirebase);
};

FirebaseDataSource.prototype.stopAllListeners = function () {
    var This = this;
    This._listeners.forEach(function (listener) {
        This._stopChildListener(listener);
    });
};

var _encodeForFirebaseKey = (function () {
    var encode = encodeURIComponent;
    var replacements = {
        ".": {re: "\\.", val: "%2E"},
        "$": {re: "$", val: encode("$")},
        "[": {re: "\\[", val: encode("[")},
        "]": {re: "\\]", val: encode("]")},
        "#": {re: "#", val: encode("#")},
        "/": {re: "/", val: encode("/")}
    };
    var replaceables = new RegExp(
        "[" + Object.keys(replacements).map(function (k) {return replacements[k].re;}).join("") + "]",
        "g"
    );
    var doReplace = function (match) {
        return replacements[match].val;
    };

    return function (userId) {
        if (typeof userId !== "string") {
            return userId;
        }

        return userId.replace(replaceables, doReplace);
    };
}());

var _timestampWritingSet = function (targetSet) {
    function timestampAtLast(path) {
        // check path before calling timestampAtLast
        var init = path.slice(0,-1);
        var last = path.slice(-1);
        last[0] = last[0] + "-timestamp";
        return init.concat(last);
    }
    return {
        cloneSetSource: function () {
            var clonedTargetSet = targetSet.cloneSetSource();
            return _timestampWritingSet(clonedTargetSet);
        },
        addOnceListener: function () {
            return targetSet.addOnceListener.apply(targetSet, arguments);
        },
        addListener: function () {
            return targetSet.addListener.apply(targetSet, arguments);
        },
        removeListener: function () {
            return targetSet.removeListener.apply(targetSet, arguments);
        },

        addChild: function () {
            targetSet.addChild.apply(targetSet, arguments);
        },
        removeChild: function () {
            targetSet.removeChild.apply(targetSet, arguments);
        },
        changeChild: function (key, path, value, callback) {
            var timestamp = Date.now();
            return targetSet.changeChild(key, path, value, function (err) {
                if (err) {
                    return callback(err);
                }

                if (!path || path.length <= 0) {
                    return callback();
                }
                return targetSet.changeChild(key, timestampAtLast(path), timestamp, callback);
            });
        }
    };
};


var _offsetValue = function (v, key) {
    if (!v) {
        return null;
    }
    return v[key];
};

var _offsetFb = function (fb, key) {
    return fb.child(_encodeForFirebaseKey(key));
};

FirebaseDataSource.prototype._startChildListener = function (fb, listener, offset) {
    var This = this;

    offset = offset || [];
    var source = {};

    var ca = fb.on("child_added", function (snapshot) {
        var v = snapshot.val();
        var k = snapshot.key;
        k = decodeURIComponent(k);
        listener.onChildAdded(source, k, offset.reduce(_offsetValue, v));
    });

    var cc = fb.on("child_changed", function (snapshot) {
        var v = snapshot.val();
        var k = snapshot.key;
        k = decodeURIComponent(k);
        listener.onChildChanged(source, k, offset.reduce(_offsetValue, v));
    });

    var cr = fb.on("child_removed", function (snapshot) {
        var v = snapshot.val();
        var k = snapshot.key;
        k = decodeURIComponent(k);
        listener.onChildRemoved(source, k, offset.reduce(_offsetValue, v));
    });

    source = {
        fb: fb,
        listenerObj: listener,
        ca: ca,
        cc: cc,
        cr: cr
    };
    This._listeners.push(source);
    return source;
};

FirebaseDataSource.prototype._onceChildListener = function (fb, listener, offset, callback) {

    const res = new Promise((resolve, reject) => {
        offset = offset || [];

        fb.once("value", snapshot => {
            var values = snapshot.val();
            for(let key in values) {
                const value = values[key];
                listener.onChildAdded({}, decodeURIComponent(key), offset.reduce(_offsetValue, value));
            }
            resolve();
        });
    });

    if (callback) res.then(() => callback(), err => { callback(err); throw err; });

    return res;

};

FirebaseDataSource.prototype._stopChildListener = function (obj) {
    var This = this;
    This._listeners.forEach(function (o) {
        if (o !== obj) {
            return;
        }

        o.fb.off("child_added", o.ca);
        o.fb.off("child_changed", o.cc);
        o.fb.off("child_removed", o.cr);
    });
};

FirebaseDataSource.prototype._fbToSetSource = function (fb, cloneMemberFn, offsets) {
    var This = this;
    offsets = offsets || [];

    return {
        cloneSetSource: function () {
            return cloneMemberFn.apply(This.cloneSource());
        },
        addOnceListener: listener => {
            return this._onceChildListener(fb, listener, offsets);
        },
        addListener: function (listener) {
            return This._startChildListener(fb, listener, offsets);
        },
        removeListener: function (obj) {
            return This._stopChildListener(obj);
        },
        addChild: function (value, callback) {
            var fbRef = fb.push();
            var offsetValue = offsets.reduceRight((value, offset) => {
                const ret = {};
                ret[offset] = value;
                return ret;
            }, value);
            return fbRef.set(offsetValue, callback);
        },
        removeChild: function (key, callback) {
            var childFb = [key].reduce(_offsetFb, fb);

            childFb = offsets.reduce(_offsetFb, childFb);
            return childFb.set(null, callback);
        },
        changeChild: function (key, path, value, callback) {
            var childFb = [key].reduce(_offsetFb, fb);
            path = path || [];

            childFb = offsets.reduce(_offsetFb, childFb);
            childFb = path.reduce(_offsetFb, childFb);
            return childFb.set(value, callback);
        }
    };
};

FirebaseDataSource.prototype.getInviteeSetSource = function () {
    var This = this;
    var fb = This._eventFirebase.child("invites").child("invitees");
    var setSource = This._fbToSetSource(fb, This.getInviteeSetSource);
    return _timestampWritingSet(setSource);
};

FirebaseDataSource.prototype.getUserSetSource = function () {
    var This = this;
    var fb = This._eventFirebase.child("users");
    var setSource = This._fbToSetSource(fb, This.getUserSetSource);
    return _timestampWritingSet(setSource);
};

FirebaseDataSource.prototype.getRsvpQuestionsSetSource = function () {
    var This = this;
    var fb = This._eventFirebase.child("rsvp").child("customQuestions");
    return This._fbToSetSource(fb, This.getRsvpQuestionsSetSource);
};

FirebaseDataSource.prototype.getRsvpOverridesSetSource = function () {
    var This = this;
    var fb = This._eventFirebase.child("rsvp").child("overrides");
    return This._fbToSetSource(fb, This.getRsvpOverridesSetSource);
};

export default FirebaseDataSource;